import { Decimal } from 'decimal.js'
import type { Impact, ImpactScale, ImpactType, IProject } from '@/api/interfaces'
import {
  getPrimaryScaleForImpactType,
  IMPACT_SCALE_BOTTLES,
  IMPACT_SCALE_KILOGRAMS,
} from '@/api/interfaces'

export const PLASTIC_BOTTLE_WEIGHT_KG = 0.02

interface ParentScaleContext {
  sourceScale: ImpactScale
  targetScale: ImpactScale
  familyRootProjectId?: string
}

export const resolveParentScaleContext = (
  impact: Impact,
  projects: IProject[],
): ParentScaleContext => {
  if (!impact.projectId) {
    const defaultProject = projects.find(
      (project) => project.type === impact.type && project.default && !project.parentProjectId,
    )
    // Missing default is a curated-catalog gap, not a runtime-validated invariant.
    const scale = defaultProject?.scale ?? getPrimaryScaleForImpactType(impact.type)
    return {
      sourceScale: scale,
      targetScale: scale,
      familyRootProjectId: defaultProject?.projectId,
    }
  }

  const project = projects.find((candidate) => candidate.projectId === impact.projectId)
  if (!project)
    throw new Error(
      `Project catalog is missing project "${impact.projectId}" for impact type "${impact.type}".`,
    )
  if (project.type !== impact.type)
    throw new Error(
      `Project "${project.projectId}" has impact type "${project.type}", not "${impact.type}".`,
    )

  if (!project.parentProjectId) {
    return {
      sourceScale: project.scale,
      targetScale: project.scale,
      familyRootProjectId: project.projectId,
    }
  }

  const parentProject = projects.find(
    (candidate) => candidate.projectId === project.parentProjectId,
  )
  if (!parentProject)
    throw new Error(
      `Project catalog is missing parent project "${project.parentProjectId}" for project "${project.projectId}".`,
    )
  // Also rejects a project naming itself as parent, which resolves to a parent carrying parentProjectId.
  if (parentProject.parentProjectId)
    throw new Error(`Parent project "${parentProject.projectId}" must be a family root.`)
  if (parentProject.type !== impact.type)
    throw new Error(
      `Parent project "${parentProject.projectId}" has impact type "${parentProject.type}", not "${impact.type}".`,
    )

  return {
    sourceScale: project.scale,
    targetScale: parentProject.scale,
    familyRootProjectId: parentProject.projectId,
  }
}

const convertParentScaleAmount = (impact: Impact, context: ParentScaleContext): number => {
  if (context.sourceScale === context.targetScale) return impact.amount

  switch (impact.type) {
    case 'plastic':
      if (
        context.sourceScale === IMPACT_SCALE_KILOGRAMS &&
        context.targetScale === IMPACT_SCALE_BOTTLES
      ) {
        return new Decimal(impact.amount)
          .dividedBy(PLASTIC_BOTTLE_WEIGHT_KG)
          .toDecimalPlaces(0)
          .toNumber()
      }
      break
    case 'trees':
    case 'carbon':
    case 'kelp':
    case 'water':
    case 'bees':
      break
    default: {
      const exhaustiveCheck: never = impact.type
      throw new Error(exhaustiveCheck)
    }
  }

  throw new Error(
    `Unsupported parent-scale conversion for impact type "${impact.type}" from "${context.sourceScale}" to "${context.targetScale}" (project "${impact.projectId || 'legacy'}").`,
  )
}

/** Sums one impact type after normalizing each native amount to its catalog family-root scale. */
export const sumParentScaleEquivalentAmount = (
  type: ImpactType,
  impacts: Impact[],
  projects: IProject[],
  options: { familyRootProjectId?: string } = {},
): number => {
  let targetScale: ImpactScale | undefined

  return impacts
    .filter((impact) => impact.type === type)
    .reduce((total, impact) => {
      const context = resolveParentScaleContext(impact, projects)
      if (
        options.familyRootProjectId &&
        context.familyRootProjectId !== options.familyRootProjectId
      ) {
        return total
      }
      if (targetScale && targetScale !== context.targetScale) {
        throw new Error(
          `Cannot sum impact type "${type}" across family-root scales "${targetScale}" and "${context.targetScale}".`,
        )
      }
      targetScale = context.targetScale
      return new Decimal(total).add(convertParentScaleAmount(impact, context)).toNumber()
    }, 0)
}
